import { Box, Loader } from '@wix/design-system';
import { WIX_STUDIO_HEADER_HEIGHT } from '@wix/core-domain-search';
import { DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useDomainPermissions } from '@wix/my-domains-shared-components/@common-plugins/useDomainPermissions';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import React, { useMemo } from 'react';
import { ResultsLayout, ResultsRecommendations, ResultsSearchBar } from '../ResultsLayout';
import { buildMobileEntriStatusesOverrides } from './mobileEntriStatusesOverrides';
import { mobileUpgradeRequireAssignStatus } from './mobileUpgradeAssignStatus';
import { LoggedInMobileVoucherBanner } from '../../Components/VoucherBanner';
import { getLoggedInMobileStatusesConfiguration } from '../../Components';
import { ExperimentSpecs } from '../../constants';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useResultsPageConfig } from '../../hooks/useResultsPageConfig';
import { mobileUpgradeRequireConnectStatus } from './mobileUpgradeConnectStatus';
export const LoggedInMobileResultsPage = _ref => {
  var _getPremiumEssentials;
  let {
    handlers,
    externalViewer = false,
    onConnectIntro,
    onConnectManual,
    onConnectError
  } = _ref;
  const isConnectEnabled = Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.LoggedInMobileConnect]);
  const {
    isV4
  } = useResultsPageConfig();
  const customConfiguration = useMemo(() => getLoggedInMobileStatusesConfiguration({
    isConnectEnabled
  }), [isConnectEnabled]);

  // Override the Entri-starting actions themselves (not just their cards) so
  // clicking a results card runs the mobile full-page divert — the same actions
  // `SelectSiteRoute` runs imperatively after a site is picked.
  const statusesOverrides = useMemo(() => isConnectEnabled ? buildMobileEntriStatusesOverrides({
    onConnectIntro,
    onConnectManual,
    onConnectError
  }) : {
    upgradeRequireAssign: mobileUpgradeRequireAssignStatus,
    upgradeRequireConnect: mobileUpgradeRequireConnectStatus
  }, [isConnectEnabled, onConnectIntro, onConnectManual, onConnectError]);
  return /*#__PURE__*/React.createElement(DomainsApiClient.Provider, null, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer
      },
      handlers,
      statusesOverrides,
      resolveCorrectedDomains: isV4
    },
    statusesCustomComponents: {
      customConfiguration
    }
  }, /*#__PURE__*/React.createElement(LoggedInMobileResultsPageContent, null)));
};
const LoggedInMobileResultsPageContent = () => {
  const {
    canManageDomains,
    isLoading,
    error,
    refetch
  } = useDomainPermissions();
  const {
    isV4
  } = useResultsPageConfig();
  const isMobile = useIsMobile();
  if (isLoading) {
    return /*#__PURE__*/React.createElement(Box, {
      height: `calc(100dvh - ${WIX_STUDIO_HEADER_HEIGHT})`,
      align: "center",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, null));
  }
  if (error) {
    return /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.TechnicalIssue, {
      onRetry: () => refetch()
    });
  }
  if (!canManageDomains()) {
    return /*#__PURE__*/React.createElement(Box, {
      height: `calc(100dvh - ${WIX_STUDIO_HEADER_HEIGHT})`
    }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.NoPermissionsBlock, null));
  }
  return /*#__PURE__*/React.createElement(ResultsLayout, {
    biViewMode: "Gate Result Page",
    reportGatePageLoadEvents: false,
    useReferralAdditionalInfoAsSearchOrigin: true,
    querySubmitPath: "/results",
    deviceType: "Smartphone",
    stickyHeaderOffset: WIX_STUDIO_HEADER_HEIGHT,
    hideSearchBarShadowOnPartialMatch: true,
    showSearchIcon: true,
    hideFilterChips: true,
    useCompactRecommendationsLayout: true,
    headerTop: isV4 && !isMobile ? WIX_STUDIO_HEADER_HEIGHT : undefined,
    headerPadding: isV4 && !isMobile ? '0 48px' : undefined,
    headerMargin: isV4 && isMobile ? '0' : undefined,
    desktopGridGap: isV4 ? 'SP2 0' : undefined,
    voucherPlacement: isV4 ? 'banner' : 'outsideStickyHeader',
    mobileVoucherInSearchBar: isV4,
    optimizedSearchBarStyle: !isV4,
    searchBar: /*#__PURE__*/React.createElement(ResultsSearchBar, {
      showUnassignedDomainsOnEmptyInput: true,
      compactLayouts: isV4
    }),
    voucher: /*#__PURE__*/React.createElement(LoggedInMobileVoucherBanner, {
      isOptimized: true
    })
  }, /*#__PURE__*/React.createElement(ResultsRecommendations, {
    showHeroCard: true,
    hideAiInsights: true,
    compactPriceLabel: isV4,
    stackedMobileCards: isV4,
    desktopContentGap: isV4 ? 'SP4' : undefined,
    insightsPaddingTop: isV4 ? 'SP4' : undefined
  }));
};
//# sourceMappingURL=LoggedInMobileResultsPage.js.map